import { useEffect, useState } from 'react';
import type { VisitTemplate } from '../data/templates';

// Stand-in for the eClinicalWorks chart-history pane. This is the surface the
// Claryn extension drives: a left nav rail of the 7 history sections, a content
// pane that shows one section at a time, a "… Verified" checkbox on the
// attestable sections, and a single Next control.
//
// The `data-hx-*` hooks here are what the extension's "demo" selector profile
// (packages/extension/src/selectors.ts) targets. Keep them in sync.
//
// The demo data is intentionally shaped to exercise every V0 flag rule:
//   • Gyn      → no LMP documented + Pap overdue (2023)
//   • OB       → count mismatch (P2 but 3 deliveries)
//   • Allergies→ orange interaction badge
//   • Family   → empty section

type HxId = 'pmh' | 'allergies' | 'gyn' | 'ob' | 'surgical' | 'family' | 'social';

interface HxDef {
  id: HxId;
  label: string;
  verifyLabel?: string; // present => attestable section with a checkbox
}

const HX_SECTIONS: HxDef[] = [
  { id: 'pmh', label: 'Past Medical History', verifyLabel: 'Hx Verified' },
  { id: 'allergies', label: 'Allergies', verifyLabel: 'Allergies Verified' },
  { id: 'gyn', label: 'Gyn History' },
  { id: 'ob', label: 'OB History' },
  { id: 'surgical', label: 'Surgical History', verifyLabel: 'Surgical Hx Verified' },
  { id: 'family', label: 'Family History' },
  { id: 'social', label: 'Social History', verifyLabel: 'Social History Verified' },
];

interface Props {
  template: VisitTemplate;
}

export function HistoryTab({ template }: Props) {
  const [activeId, setActiveId] = useState<HxId>('pmh');
  const [verified, setVerified] = useState<Record<string, boolean>>({});

  // Reset attestation state when the patient changes.
  useEffect(() => {
    setActiveId('pmh');
    setVerified({});
  }, [template]);

  const activeDef = HX_SECTIONS.find((s) => s.id === activeId)!;

  const goNext = () => {
    const idx = HX_SECTIONS.findIndex((s) => s.id === activeId);
    if (idx < HX_SECTIONS.length - 1) setActiveId(HX_SECTIONS[idx + 1].id);
  };

  const toggleVerify = (id: HxId) => {
    setVerified((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="hx-root" data-hx-root>
      <aside className="hx-rail">
        {HX_SECTIONS.map((s) => {
          const done = verified[s.id];
          const isActive = s.id === activeId;
          return (
            <button
              key={s.id}
              type="button"
              data-hx-nav={s.id}
              className={`hx-nav-item${isActive ? ' active' : ''}${done ? ' verified' : ''}`}
              onClick={() => setActiveId(s.id)}
            >
              <span className="hx-nav-check">{done ? '✓' : ''}</span>
              <span className="hx-nav-label">{s.label}</span>
            </button>
          );
        })}
      </aside>

      <section className="hx-main">
        <header className="hx-head">
          <h2 className="hx-title">{activeDef.label}</h2>
          {activeDef.verifyLabel && (
            <label className="hx-verify">
              <input
                type="checkbox"
                data-hx-verify={activeId}
                checked={!!verified[activeId]}
                onChange={() => toggleVerify(activeId)}
              />
              {activeDef.verifyLabel}
            </label>
          )}
        </header>

        <div className="hx-pane" data-hx-pane={activeId}>
          {renderContent(activeId, template)}
        </div>

        <footer className="hx-foot">
          <span className="hx-foot-hint">{template.patientName}</span>
          <button type="button" className="hx-next" data-hx-next onClick={goNext}>
            Next ›
          </button>
        </footer>
      </section>
    </div>
  );
}

// ── Section content ────────────────────────────────────────────────────────
// Demo data mirroring the Elena Ramirez chart. Free text so the extension reads
// it the same way it would read eCW's rendered panels.

function renderContent(id: HxId, template: VisitTemplate): JSX.Element {
  switch (id) {
    case 'pmh':
      return (
        <ul className="hx-list">
          <li>Mild intermittent asthma, uncomplicated</li>
          <li>Morbid obesity due to excess calories (BMI 39.87)</li>
          <li>Elevated blood pressure, 145/88 mmHg</li>
          <li>Diverticulosis — colonoscopy 03/10/2025</li>
          <li>Vitamin D deficiency</li>
        </ul>
      );

    case 'allergies':
      return (
        <div className="hx-block">
          <p>{template.allergies?.drug ?? 'No known drug allergies (NKDA)'}</p>
          <p>{template.allergies?.food ?? 'No food allergies recorded'}</p>
          <p>{template.allergies?.environmental ?? 'No environmental allergies recorded'}</p>
          {/* Orange interaction badge — drives the interaction flag rule. */}
          <div className="hx-interaction" data-hx-interaction>
            ⚠ Interaction: Metoprolol ↔ Albuterol — monitor
          </div>
        </div>
      );

    case 'gyn':
      // No LMP, Pap last documented 2023 → two flags.
      return (
        <div className="hx-block">
          <p>Menarche age 12. Cycles historically regular.</p>
          <p>Last Pap 2023 — normal, HPV negative.</p>
          <p>Contraception: Junel 1/20 (norethindrone / ethinyl estradiol) daily.</p>
        </div>
      );

    case 'ob':
      // G3 P2 but three documented deliveries → count mismatch.
      return (
        <div className="hx-block">
          <p>G3 P2.</p>
          <p>Deliveries: NSVD x2 (2008, 2011), C-section x1 (2015).</p>
          <p>No antepartum or postpartum complications noted.</p>
        </div>
      );

    case 'surgical':
      return (
        <ul className="hx-list">
          <li>Cesarean section (2015)</li>
          <li>Cholecystectomy (2012)</li>
          <li>Liposuction and breast augmentation (2018)</li>
        </ul>
      );

    case 'family':
      // Intentionally empty → exercises the empty-section flag.
      return <></>;

    case 'social':
      return (
        <ul className="hx-list">
          {(template.socialHistory ?? []).map((item) => (
            <li key={item.category}>
              <span className="hx-social-cat">{item.category}:</span> {item.value}
            </li>
          ))}
        </ul>
      );
  }
}
